// src/Components/Streak/StreakCelebration.tsx
// The once-a-day moment. The first qualifying activity of the local day opens
// this full-screen scene: the flame bursts in, the number rolls from n-1 to n,
// today's dot in the week strip lights up, one line ("Day 8! Your longest
// yet"), progress to the next milestone, and call-outs for a freeze earned, a
// freeze that saved the day, a new flame tier or a milestone reached.
// Continue closes it. Reduced motion: no burst, no roll, a plain fade.
import ShareButton from "../Share/ShareButton";
import React, { useEffect, useMemo, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Portal from "../Utils/Portal";
import Icon from "../Ui/Icon";
import Pill from "../Ui/Pill";
import StreakFlame from "./StreakFlame";
import StreakWeekStrip from "./StreakWeekStrip";
import RollingNumber from "./RollingNumber";
import RewardChips from "./RewardChips";
import { celebrationLine, isTierUp, StreakResult, tierFor } from "./streakModel";

interface Props {
  result: StreakResult;
  onClose: () => void;
  onOpenStreak: () => void;
}

const SPRAY = Array.from({ length: 14 }, (_, i) => {
  const a = (i / 14) * Math.PI * 2;
  const r = 110 + (i % 3) * 30;
  return { x: Math.cos(a) * r, y: Math.sin(a) * r * 0.8 - 30, s: 6 + (i % 4) * 2, d: 0.28 + (i % 5) * 0.04 };
});

const StreakCelebration: React.FC<Props> = ({ result, onClose, onOpenStreak }) => {
  const reduce = useReducedMotion();
  const continueRef = useRef<HTMLButtonElement>(null);
  const tier = tierFor(result.count);
  const line = celebrationLine(result);
  const next = result.nextMilestone;

  // Progress bar to the next milestone, before and after today.
  const progress = useMemo(() => {
    if (!next) return null;
    const span = next.days;
    const pctAfter = Math.max(0, Math.min(1, (span - next.remaining) / span));
    const pctBefore = Math.max(0, Math.min(1, (span - next.remaining - 1) / span));
    return { pctBefore, pctAfter };
  }, [next]);

  useEffect(() => {
    const t = window.setTimeout(() => continueRef.current?.focus(), reduce ? 0 : 900);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, reduce]);

  const enter = (delay: number) =>
    reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.2 } }
      : { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { delay, duration: 0.45, ease: "easeOut" } };

  return (
    <Portal>
      <motion.div
        className={`streak-celebrate streak-celebrate--${tier.key}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="streak-celebrate-title"
        data-testid="streak-celebration"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: reduce ? 0.15 : 0.3 }}
      >
        <div className="streak-celebrate__bg" aria-hidden="true" />
        <div className="streak-celebrate__body">
          <div className="streak-celebrate__stage" aria-hidden="true">
            {!reduce && (
              <>
                <motion.span
                  className="streak-celebrate__ring"
                  initial={{ scale: 0.2, opacity: 0.9 }}
                  animate={{ scale: 2.4, opacity: 0 }}
                  transition={{ delay: 0.25, duration: 0.9, ease: "easeOut" }}
                />
                <motion.span
                  className="streak-celebrate__ring streak-celebrate__ring--late"
                  initial={{ scale: 0.2, opacity: 0.7 }}
                  animate={{ scale: 1.9, opacity: 0 }}
                  transition={{ delay: 0.45, duration: 1, ease: "easeOut" }}
                />
                {SPRAY.map((p, i) => (
                  <motion.span
                    key={i}
                    className="streak-celebrate__spark"
                    style={{ width: p.s, height: p.s }}
                    initial={{ x: 0, y: 0, opacity: 0, scale: 1 }}
                    animate={{ x: p.x, y: p.y, opacity: [0, 1, 0], scale: [1, 1, 0.3] }}
                    transition={{ delay: p.d, duration: 1.1, ease: "easeOut" }}
                  />
                ))}
              </>
            )}
            <motion.div
              className="streak-celebrate__flame"
              initial={reduce ? { opacity: 0 } : { scale: 0.15, y: 50, opacity: 0, rotate: -6 }}
              animate={reduce ? { opacity: 1 } : { scale: [0.15, 1.2, 0.96, 1], y: 0, opacity: 1, rotate: 0 }}
              transition={reduce ? { duration: 0.2 } : { duration: 0.95, times: [0, 0.55, 0.8, 1], ease: "easeOut" }}
            >
              <StreakFlame count={result.count} size={190} />
            </motion.div>
          </div>

          <div className="streak-celebrate__count" data-testid="streak-celebration-count">
            <RollingNumber from={result.previousCount} to={result.count} delay={0.75} />
          </div>
          <motion.div className="streak-celebrate__unit" {...enter(0.5)}>
            day streak
          </motion.div>
          <motion.h2 id="streak-celebrate-title" className="streak-celebrate__line" {...enter(1.05)}>
            {line}
          </motion.h2>

          <motion.div className="streak-celebrate__week" {...enter(1.2)}>
            <StreakWeekStrip days={result.weekStrip} lightTodayAfter={1.45} />
          </motion.div>

          <motion.div className="streak-celebrate__callouts" {...enter(1.7)}>
            {isTierUp(result.count) && (
              <Pill tone="accent" icon={<Icon name="fire" size={14} filled />} className="streak-callout streak-callout--tier">
                Your flame grew into {tier.name}
              </Pill>
            )}
            {result.earnedFreeze && (
              <span className="streak-callout streak-callout--ice" data-testid="streak-callout-freeze">
                <Icon name="snowflake" size={18} />
                Freeze earned. It covers one missed day for you.
              </span>
            )}
            {result.usedFreezes > 0 && (
              <span className="streak-callout streak-callout--ice">
                <Icon name="snowflake" size={18} />
                {result.usedFreezes === 1 ? "A freeze covered the day you missed." : `${result.usedFreezes} freezes covered the days you missed.`}
              </span>
            )}
            {result.milestoneReached && (
              <div className="streak-callout streak-callout--milestone" data-testid="streak-callout-milestone">
                <span className="streak-callout__title">
                  <Icon name="trophy" size={18} />
                  {result.milestoneReached.days} day milestone reached
                </span>
                <RewardChips reward={result.milestoneReached.reward} />
              </div>
            )}
          </motion.div>

          {next && progress && (
            <motion.div className="streak-celebrate__next" {...enter(1.9)}>
              <div className="streak-celebrate__next-label">
                <span>
                  {next.remaining === 0
                    ? `${next.days} day milestone`
                    : `${next.remaining} ${next.remaining === 1 ? "day" : "days"} to the ${next.days} day milestone`}
                </span>
                <span className="streak-celebrate__next-num">
                  {next.days - next.remaining}/{next.days}
                </span>
              </div>
              <div className="streak-progress" role="progressbar" aria-valuemin={0} aria-valuemax={next.days} aria-valuenow={next.days - next.remaining}>
                <motion.span
                  className="streak-progress__fill"
                  initial={{ width: `${(reduce ? progress.pctAfter : progress.pctBefore) * 100}%` }}
                  animate={{ width: `${progress.pctAfter * 100}%` }}
                  transition={{ delay: reduce ? 0 : 2.1, duration: 0.8, ease: "easeOut" }}
                />
              </div>
            </motion.div>
          )}

          <motion.div className="streak-celebrate__actions" {...enter(2.1)}>
            <button ref={continueRef} type="button" className="mr-btn mr-btn--accent mr-btn--block" onClick={onClose} data-testid="streak-celebration-continue">
              Continue
            </button>
            <ShareButton
              block
              variant="secondary"
              before={onClose}
              spec={{ kind: "streak", days: result.count, milestone: !!result.milestoneReached }}
              testId="streak-celebration-share"
            />
            <button type="button" className="mr-btn mr-btn--ghost mr-btn--block streak-celebrate__more" onClick={onOpenStreak}>
              {result.milestoneReached ? "Claim on the Streak page" : "See my streak"}
            </button>
          </motion.div>
        </div>
      </motion.div>
    </Portal>
  );
};

export default StreakCelebration;
